import { AppRoutes } from '../../../common/constants';
import { FnaBuyAndSellNeeds, FNADetails, FnaPolicyStructure } from '../../../common/types/analyses';
import { JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import AlternativeLayout from './alternativeLayout';
import Content from '../../../common/components/content';
import FnaPageHeaderSummary from '../../components/fnaPageHeaderSummary';
import InlineError from '../../../common/components/inlineError';
import Layout from '../../../common/components/layout';
import LayoutLoadingState from '../../../common/components/layout/layoutLoadingState';
import ShareBuyBackLayout from './shareBuyBackLayout';
import StepFooter from '../../../common/components/stepFooter';
import TraditionalLayout from './traditionalLayout';

/**
 * Display the FNA buy and sell needs results (calculations)
 */
const FnaBuyAndSellNeedPage = (): JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: fna } = useQuery<FNADetails>({
    queryKey: [`/FNADetail/${id}`],
    enabled: Boolean(id),
  });

  const {
    data: buyAndSellNeeds,
    error,
    isLoading,
  } = useQuery<FnaBuyAndSellNeeds>({
    queryKey: [`/FNABuyandsellNeeds/${id}`],
    enabled: Boolean(id),
    retry: false, // disable retrying to get an error immediately
  });

  if (!fna || isLoading) {
    return <LayoutLoadingState />;
  }

  const fnaRoleplayers =
    fna.roleplayers.map(({ roleplayerName }) => roleplayerName).join(', ') || '-';
  const breadcrumbs = [
    { label: 'Financial needs analyses', href: AppRoutes.FNAs },
    { label: fnaRoleplayers, href: `${AppRoutes.FNAs}/${fna.id}` },
    { label: 'Buy-and-Sell needs', href: `${AppRoutes.FNAs}/${fna.id}/buy-and-sell` },
  ];

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        {error ? (
          <InlineError
            error={error}
            actionLabel="Go back"
            actionUrl={`${AppRoutes.FNAs}/${fna.id}/buy-and-sell`}
          />
        ) : (
          <>
            <FnaPageHeaderSummary
              fna={fna}
              title={`Buy-and-Sell needs - ${fna.buyandsell?.policyStructureNote}`}
            />
            {fna.buyandsell?.policyStructureID === FnaPolicyStructure.Traditional && (
              <TraditionalLayout fna={fna} buyAndSellNeeds={buyAndSellNeeds} />
            )}
            {fna.buyandsell?.policyStructureID === FnaPolicyStructure.Alternative && (
              <AlternativeLayout fna={fna} buyAndSellNeeds={buyAndSellNeeds} />
            )}
            {fna.buyandsell?.policyStructureID === FnaPolicyStructure.ShareBuyBack && (
              <ShareBuyBackLayout fna={fna} buyAndSellNeeds={buyAndSellNeeds} />
            )}
            <StepFooter
              isLoading={false}
              nextButtonTitle="Done"
              onClickNext={(): void => navigate(AppRoutes.FNAs)}
              onClickBack={(): void => navigate(`${AppRoutes.FNAs}/${fna.id}/buy-and-sell`)}
            />
          </>
        )}
      </Content>
    </Layout>
  );
};

export default FnaBuyAndSellNeedPage;
